import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { productsService } from '../services/api';
import config from '../config';
import StockDetailsModal from '../components/products/StockDetailsModal';

const formatPrice = (value) =>
  typeof value === 'number' && !Number.isNaN(value)
    ? `${value.toFixed(2)}€`
    : 'N/A';

const normalizeVariants = (product) => {
  if (!product) return [];
  const variants = product.variants || product.product_variants || [];
  return Array.isArray(variants) ? variants : [];
};

const getProductPriceValue = (product, field) => {
  if (!product) return null;
  const alt = field === 'priceTtc' ? 'price_ttc' : 'price_ht';
  const raw = product[field];
  const fallback = product[alt];
  const numeric = Number(raw !== undefined ? raw : fallback);
  return Number.isFinite(numeric) ? numeric : null;
};

const getVariantPriceRange = (product, field) => {
  const variants = normalizeVariants(product);
  if (variants.length < 2) {
    return null;
  }

  const fieldAlt = field === 'priceTtc' ? 'price_ttc' : 'price_ht';
  const prices = variants
    .map((variant) => {
      const raw = variant[field];
      const fallback = variant[fieldAlt];
      const value = raw !== undefined ? raw : fallback;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    })
    .filter((value) => value !== null);

  if (prices.length < 2) {
    return null;
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  if (min === max) {
    return { single: min };
  }

  return { min, max };
};

const getPriceDisplay = (product, field) => {
  const range = getVariantPriceRange(product, field);
  if (range?.single !== undefined) {
    return formatPrice(range.single);
  }
  if (range) {
    return `${formatPrice(range.min)} - ${formatPrice(range.max)}`;
  }
  const value = getProductPriceValue(product, field);
  return formatPrice(value);
};

const getVariantAvgCostRange = (product) => {
  const variants = normalizeVariants(product);
  if (!Array.isArray(variants) || variants.length === 0) return null;
  const costs = variants
    .map((variant) => Number(variant?.avgStockUnitCostHt))
    .filter((value) => Number.isFinite(value) && value >= 0);
  if (costs.length === 0) return null;
  const min = Math.min(...costs);
  const max = Math.max(...costs);
  if (min === max) return { single: min };
  return { min, max };
};

const getAvgPurchaseCostDisplay = (product) => {
  const variantRange = getVariantAvgCostRange(product);
  if (variantRange?.single !== undefined) return formatPrice(variantRange.single);
  if (variantRange) return `${formatPrice(variantRange.min)} - ${formatPrice(variantRange.max)}`;
  const productAvg = Number(product?.avgStockUnitCostHt);
  if (Number.isFinite(productAvg) && productAvg >= 0) return formatPrice(productAvg);
  return Number(product?.stock || 0) <= 0 ? 'Pas de stock' : 'N/A';
};

const getMinVariantValue = (product, field) => {
  const variants = normalizeVariants(product);
  if (!Array.isArray(variants) || variants.length === 0) return null;
  const values = variants
    .map((variant) => Number(variant?.[field]))
    .filter((value) => Number.isFinite(value));
  if (values.length === 0) return null;
  return Math.min(...values);
};

const getSortableValue = (product, sortBy) => {
  switch (sortBy) {
    case 'name':
      return String(product?.name || '');
    case 'priceHt': {
      const variantMin = getMinVariantValue(product, 'priceHt');
      if (variantMin != null) return variantMin;
      return Number(getProductPriceValue(product, 'priceHt'));
    }
    case 'avgStockUnitCostHt': {
      const variantRange = getVariantAvgCostRange(product);
      if (variantRange?.single !== undefined) return variantRange.single;
      if (variantRange?.min !== undefined) return variantRange.min;
      const value = Number(product?.avgStockUnitCostHt);
      return Number.isFinite(value) ? value : null;
    }
    case 'priceTtc': {
      const variantMin = getMinVariantValue(product, 'priceTtc');
      if (variantMin != null) return variantMin;
      return Number(getProductPriceValue(product, 'priceTtc'));
    }
    case 'marginPercent': {
      const extremes = getVariantMarginExtremes(product);
      if (extremes?.min?.marginPercent != null) return Number(extremes.min.marginPercent);
      const value = Number(product?.marginPercent);
      return Number.isFinite(value) ? value : null;
    }
    case 'stock': {
      const value = Number(product?.stock);
      return Number.isFinite(value) ? value : null;
    }
    case 'status':
      return product?.isActive ? 1 : 0;
    default:
      return null;
  }
};

const getMarginColor = (marginPercent) => {
  if (marginPercent == null || Number.isNaN(Number(marginPercent))) return 'default';
  if (Number(marginPercent) <= 10) return 'error';
  if (Number(marginPercent) <= 50) return 'warning';
  return 'success';
};

const getMarkPercent = (sellingPriceHt, avgCostHt) => {
  const selling = Number(sellingPriceHt);
  const cost = Number(avgCostHt);
  if (!Number.isFinite(selling) || selling <= 0) return null;
  if (!Number.isFinite(cost) || cost < 0) return null;
  const mark = ((selling - cost) / selling) * 100;
  return Number.isFinite(mark) ? mark : null;
};

const getVariantMarginExtremes = (product) => {
  const variants = normalizeVariants(product);
  if (!Array.isArray(variants) || variants.length === 0) return null;
  const withMargin = variants
    .filter((variant) => variant?.marginPercent !== null && variant?.marginPercent !== undefined)
    .map((variant) => ({
      ...variant,
      marginPercent: Number(variant.marginPercent),
    }))
    .filter((variant) => Number.isFinite(variant.marginPercent));
  if (withMargin.length === 0) return null;
  const sorted = [...withMargin].sort((a, b) => a.marginPercent - b.marginPercent);
  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
  };
};

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listState = location.state?.fromProductEdit ? location.state : null;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(listState?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(listState?.rowsPerPage ?? 10);
  const [searchTerm, setSearchTerm] = useState(listState?.searchTerm ?? '');
  const [searchInput, setSearchInput] = useState(listState?.searchTerm ?? '');
  const [sortBy, setSortBy] = useState(listState?.sortBy ?? 'priceHt');
  const [sortOrder, setSortOrder] = useState(listState?.sortOrder ?? 'asc');
  const [stockFilterMode, setStockFilterMode] = useState(listState?.stockFilterMode ?? 'all');
  const [pagination, setPagination] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);

  // Stock Modal State
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedProductForStock, setSelectedProductForStock] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchTerm]);

  // Garder l'état de la liste dans l'historique pour que "Retour" du navigateur restaure la page
  useEffect(() => {
    navigate('/products', { state: getListState(), replace: true });
  }, [page, rowsPerPage, searchTerm, sortBy, sortOrder, stockFilterMode]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Pour un tri global fiable, on récupère un lot large puis on trie/pagine côté front.
      // La pagination serveur seule ne permettrait de trier que la page active.
      const requestedLimit = 5000;

      // Utiliser la recherche si un terme de recherche est fourni
      const serviceCall = searchTerm.trim()
        ? productsService.search(searchTerm, {
          page: 1,
          limit: requestedLimit
        })
        : productsService.getAll({
          page: 1,
          limit: requestedLimit
        });

      const response = await serviceCall;

      // Gérer la nouvelle structure avec pagination
      if (response.data.products && response.data.pagination) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
        setTotalProducts(response.data.pagination.totalProducts);
      } else if (Array.isArray(response.data)) {
        // Fallback pour l'ancienne structure
        setProducts(response.data);
        setPagination(null);
        setTotalProducts(response.data.length);
      } else {
        console.error('Structure de réponse inattendue:', response.data);
        setProducts([]);
        setPagination(null);
        setTotalProducts(0);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setProducts([]);
      setPagination(null);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
      setSearchTerm(searchInput.trim());
    }
  };

  const handleSearchBlur = () => {
    // Optionnel : mettre à jour la recherche quand on quitte le champ
  };

  const getListState = () => ({
    fromProductEdit: true,
    page,
    rowsPerPage,
    searchTerm,
    sortBy,
    sortOrder,
    stockFilterMode,
  });

  const handleCycleStockFilter = () => {
    setStockFilterMode((prev) => {
      if (prev === 'all') return 'zero';
      if (prev === 'zero') return 'inStock';
      return 'all';
    });
    setPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortOrder('asc');
  };

  const handleEditProduct = (productId) => {
    navigate(`/products/edit/${productId}`, { state: getListState() });
  };

  const handleNewProduct = () => {
    navigate('/products/new', { state: getListState() });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await productsService.delete(id);
        fetchData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleOpenStockModal = (event, product) => {
    event.stopPropagation();
    if (product.variants && product.variants.length > 0) {
      setSelectedProductForStock(product);
      setStockModalOpen(true);
    }
  };

  const filteredProducts = products.filter((product) => {
    const stock = Number(product?.stock || 0);
    if (stockFilterMode === 'zero') return stock <= 0;
    if (stockFilterMode === 'inStock') return stock > 0;
    return true;
  });

  // Tri côté front pour préserver les autres flux existants sans impacter l'API
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aValue = getSortableValue(a, sortBy);
    const bValue = getSortableValue(b, sortBy);

    // Nulls en fin de tri
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    let compare = 0;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      compare = aValue - bValue;
    } else {
      compare = String(aValue).localeCompare(String(bValue), 'fr', { sensitivity: 'base' });
    }

    return sortOrder === 'asc' ? compare : -compare;
  });

  const totalPagesForPagination = Math.max(1, Math.ceil((sortedProducts.length || 0) / rowsPerPage));
  const paginatedProducts = sortedProducts.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => {
    const maxPageIndex = Math.max(0, totalPagesForPagination - 1);
    if (page > maxPageIndex) {
      setPage(maxPageIndex);
    }
  }, [page, totalPagesForPagination]);

  const getProductImage = (product) => {
    const baseUrl = config.API_URL?.replace(/\/$/, '');
    const imageCandidate = product.image_url || product.imageUrl || product.thumbnail || product.coverImage || product.cover_url;
    const relationImage = product.images?.length ? product.images[0].url || product.images[0].path || product.images[0].full_path : null;
    const selected = imageCandidate || relationImage;
    if (!selected) return null;
    if (/^https?:\/\//i.test(selected)) {
      return selected;
    }
    if (baseUrl) {
      return `${baseUrl}${selected.startsWith('/') ? '' : '/'}${selected}`;
    }
    return selected;
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des produits..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Gestion des Produits
          </Typography>
          {pagination && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {searchTerm ? `Résultats pour "${searchTerm}": ` : ''}
              {pagination.totalProducts} produit{pagination.totalProducts > 1 ? 's' : ''}
              {pagination.totalPages > 1 && ` • Page ${pagination.currentPage}/${pagination.totalPages}`}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewProduct}
        >
          Nouveau Produit
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher un produit..."
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={handleCycleStockFilter}>
            {stockFilterMode === 'all'
              ? 'Stock: Tous'
              : stockFilterMode === 'zero'
                ? 'Stock: 0 uniquement'
                : 'Stock: En stock'}
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={72}>Image</TableCell>
              <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'priceHt' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'priceHt'}
                  direction={sortBy === 'priceHt' ? sortOrder : 'asc'}
                  onClick={() => handleSort('priceHt')}
                >
                  Prix HT
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'avgStockUnitCostHt' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'avgStockUnitCostHt'}
                  direction={sortBy === 'avgStockUnitCostHt' ? sortOrder : 'asc'}
                  onClick={() => handleSort('avgStockUnitCostHt')}
                >
                  Achat moyen HT
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'priceTtc' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'priceTtc'}
                  direction={sortBy === 'priceTtc' ? sortOrder : 'asc'}
                  onClick={() => handleSort('priceTtc')}
                >
                  Prix TTC
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'marginPercent' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'marginPercent'}
                  direction={sortBy === 'marginPercent' ? sortOrder : 'asc'}
                  onClick={() => handleSort('marginPercent')}
                >
                  Marge %
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'stock' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'stock'}
                  direction={sortBy === 'stock' ? sortOrder : 'asc'}
                  onClick={() => handleSort('stock')}
                >
                  Stock
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortOrder : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.map((product) => {
              const imageSrc = getProductImage(product);
              return (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {imageSrc ? (
                        <Box
                          component="img"
                          src={imageSrc}
                          alt={product.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Aucune image
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {product.description?.substring(0, 50)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getPriceDisplay(product, 'priceHt')}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {getAvgPurchaseCostDisplay(product)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {getPriceDisplay(product, 'priceTtc')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const extremes = getVariantMarginExtremes(product);
                      if (extremes) {
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Chip
                              size="small"
                              color={getMarginColor(extremes.min.marginPercent)}
                              label={`Marge min: ${extremes.min.name} (${Number(extremes.min.marginPercent).toFixed(2)}%)`}
                              sx={{
                                '& .MuiChip-label': {
                                  fontWeight: 800,
                                  letterSpacing: 0.2,
                                  fontSize: '0.82rem',
                                },
                              }}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              color={getMarginColor(extremes.min.marginPercent)}
                              label={`Marque: ${(() => {
                                const mark = getMarkPercent(extremes.min.priceHt, extremes.min.avgStockUnitCostHt);
                                return mark == null ? 'N/A' : `${mark.toFixed(2)}%`;
                              })()}`}
                              sx={{
                                '& .MuiChip-label': {
                                  fontWeight: 800,
                                  letterSpacing: 0.2,
                                  fontSize: '0.82rem',
                                },
                              }}
                            />
                            <Chip
                              size="small"
                              color={getMarginColor(extremes.max.marginPercent)}
                              label={`Marge max: ${extremes.max.name} (${Number(extremes.max.marginPercent).toFixed(2)}%)`}
                              sx={{
                                '& .MuiChip-label': {
                                  fontWeight: 800,
                                  letterSpacing: 0.2,
                                  fontSize: '0.82rem',
                                },
                              }}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              color={getMarginColor(extremes.max.marginPercent)}
                              label={`Marque: ${(() => {
                                const mark = getMarkPercent(extremes.max.priceHt, extremes.max.avgStockUnitCostHt);
                                return mark == null ? 'N/A' : `${mark.toFixed(2)}%`;
                              })()}`}
                              sx={{
                                '& .MuiChip-label': {
                                  fontWeight: 800,
                                  letterSpacing: 0.2,
                                  fontSize: '0.82rem',
                                },
                              }}
                            />
                          </Box>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            size="small"
                            color={getMarginColor(product.marginPercent)}
                            label={
                              product.marginPercent == null || Number.isNaN(Number(product.marginPercent))
                                ? (Number(product.stock || 0) <= 0 ? 'Marge: Pas de stock' : 'Marge: N/A')
                                : `Marge: ${Number(product.marginPercent).toFixed(2)}%`
                            }
                            sx={{
                              '& .MuiChip-label': {
                                fontWeight: 800,
                                letterSpacing: 0.2,
                                fontSize: '0.82rem',
                              },
                            }}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            color={getMarginColor(product.marginPercent)}
                            label={`Marque: ${(() => {
                              const mark = getMarkPercent(product.priceHt, product.avgStockUnitCostHt);
                              if (mark == null) return Number(product.stock || 0) <= 0 ? 'Pas de stock' : 'N/A';
                              return `${mark.toFixed(2)}%`;
                            })()}`}
                            sx={{
                              mt: 0.35,
                              '& .MuiChip-label': {
                                fontWeight: 800,
                                letterSpacing: 0.2,
                                fontSize: '0.82rem',
                              },
                            }}
                          />
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {product.variants && product.variants.length > 0 ? (
                      <Chip
                        label={`${product.stock} (Var.)`}
                        color={product.stock < (product.stockMin || 5) ? 'warning' : 'info'}
                        size="small"
                        onClick={(e) => handleOpenStockModal(e, product)}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiChip-label': {
                            fontWeight: 800,
                            letterSpacing: 0.2,
                          },
                        }}
                      />
                    ) : (
                      <Chip
                        label={`${product.stock} ${product.stock < (product.stockMin || 5) ? '(Bas)' : ''}`}
                        color={product.stock < (product.stockMin || 5) ? 'error' : 'success'}
                        size="small"
                        sx={{
                          '& .MuiChip-label': {
                            fontWeight: 800,
                            letterSpacing: 0.2,
                          },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.isActive ? 'Actif' : 'Inactif'}
                      color={product.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{
                        '& .MuiChip-label': {
                          fontWeight: 800,
                          letterSpacing: 0.2,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditProduct(product.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(product.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={sortedProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Produits par page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </TableContainer>

      {totalPagesForPagination > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPagesForPagination}
            page={page + 1}
            onChange={(_, value) => setPage(Math.max(0, value - 1))}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </Stack>
      )}

      <StockDetailsModal
        open={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        product={selectedProductForStock}
      />
    </Box>
  );
};

export default Products;
